import { DateTime } from "luxon";
import { fenceValueWithVat, otherValueWithVat } from "@/utils";
import { pdf_contract, CONTRACT_FORM_CODE } from "@/documents/contract/pdf_contract.js";
import { moduleSizeText, moduleHeightText, moduleNameKey, isPost } from "@/utils/fenceModules";

export default {
  create(contract, executantDetails, firmaMontajContract, aditionalExtra) {
    return {
      contract,
      executantDetails,
      firmaMontajContract,
      aditionalExtra,

      // The offer this act replaces: the contract's own for act 1, the one the previous act
      // brought after it. Act 1 keeps reading the contract, as it did when a contract had one act.
      get previousOffer() {
        return this.aditionalExtra.previous || this.contract
      },

      get previousOfferId() {
        const previous = this.aditionalExtra.previous
        return previous ? previous.new_offer_id : this.contract.oferta_id
      },

      get previousOfferDate() {
        const previous = this.aditionalExtra.previous
        return DateTime.fromISO(String(previous ? previous.date_new_offer : this.contract.oferta_data)).toFormat("dd.MM.yyyy")
      },

      // Where the replaced offer comes from, as said after its price in Art.2
      get previousOfferOrigin() {
        const previous = this.aditionalExtra.previous
        if (!previous) return "pe baza careia a fost intocmit contractul"
        const date = DateTime.fromISO(String(previous.data)).toFormat("dd.MM.yyyy")
        return `adusa prin actul aditional nr. ${previous.id_aditional} / ${date}`
      },
      
      get language() {
        return 'de-DE'
      },

      get isMontajSeparat() {
        return this.contract.detalii.montaj_separat
      },

      get montajFirma1Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI DESIGN S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022003676350, cod fiscal 46619944 (neplatitor de TVA), cont bancar: RO74BTRLRONCRT0CH8981101, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get montajFirma2Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI PARTENER S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022005419357, cod fiscal 47219300 (neplatitor de TVA), cont bancar: RO14BTRLRONCRT0664679101, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get montajFirma3Text()  {
        if (!this.firmaMontajContract) return ''
        return `2. S.C. CUPROLLI PROIECTE S.R.L., cu sediul in Ghiroda, Str. Caprioarei nr. 11, Jud. Timis, inregistrata in Registrul Comertului sub numarul J2022004612351, cod fiscal 46926167 (neplatitor de TVA), cont bancar: RO11BTRLRONCRT0661673901, Banca Transilvania, reprezentata legal prin Rosu Octavian, in calitate de MONTATOR`;
      },

      get actClient() {
        if (this.contract.detalii.alt_document) {
          return this.contract.detalii.alt_document
        }
        return `C.I. serie ${this.contract.detalii.serie_ci} nr. ${this.contract.detalii.numar_ci}`
      },

      get adresaClient() {
        if (this.contract.persoana) {
          return `cu sediul in ${this.contract.adresa}, inregistrata la Registrul Comertului, sub numarul ${this.contract.reg_com}, cod fiscal ${this.contract.cui}, reprezentata legal prin ${this.contract.persoana},`
        } else {
          return `cu domiciliul in ${this.contract.detalii.adresa}, identificat cu ${this.actClient}, ${this.contract.detalii.cnp ? "CNP " + this.contract.detalii.cnp + ", " : ""}`
        }
      },

      get exemplareContract() {
        return this.isMontajSeparat ? "3 (trei)" : "2 (doua)"
      },
      
      get moduleGard() {
        return this.aditionalExtra.gard_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              // a post has no width, so it is quoted by height alone
              text: isPost(modul.tip)
                ? `${this.denumireModulGard(modul)} - ${modul.buc} buc, H ${moduleHeightText(modul)} mm;`
                : `${this.denumireModulGard(modul)} - ${modul.buc} buc cu dimensiunile L x h de ${moduleSizeText(modul)} mm;`
            }]
          }]
        }))
      },

      get moduleCustom() {
        if (!this.aditionalExtra.alte_obj) return []
        return this.aditionalExtra.alte_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              text: `${modul.denumire} - ${modul.cantitate} ${modul.um}`
            }]
          }]
        }))
      },

      get executantFirmaText() {
        return `1. S.C. ${this.executantDetails.name}, cu sediul in ${this.executantDetails.address}, inregistrata la Registrul Comertului sub numarul ${this.executantDetails.regCom}, cod fiscal ${this.executantDetails.cui}, cont bancar: ${this.executantDetails.bank_account}, ${this.executantDetails.bank_name}, telefon: ${this.executantDetails.phone}, reprezentata legal prin ${this.executantDetails.legal_representative} in calitate de EXECUTANT`
      },

      // What a module is called in this contract, keyed by type and variant. Adding a type is a
      // key here and a line in moduleNameKey - never another branch below.
      moduleNames: {
        panel: "Panou",
        pedestrianGate: "Poartă pietonală",
        pedestrianGateSidePanel: "Poartă pietonală cu panou lateral",
        swingGateSingleLeaf: "Poartă auto batantă 1 canat",
        swingGateDoubleLeaf: "Poartă auto batantă 2 canate",
        swingGateBiFoldSingle: "Poartă auto batantă 1 canat bi-fold",
        swingGateBiFoldDouble: "Poartă auto batantă 2 canate bi-fold",
        slidingGateSingleLeaf: "Poartă auto culisantă 1 canat",
        slidingGateDoubleLeaf: "Poartă auto culisantă 2 canate",
        slidingGateTelescopic: "Poartă auto culisantă telescopică",
        slidingGateArticulated: "Poartă auto culisantă articulată",
        selfSupportingGate: "Poartă auto autoportantă",
        posts: "Stâlpi",
        post80: "Stâlp 80 × 80 mm",
        post100: "Stâlp 100 × 100 mm",
        post200: "Stâlp 200 × 100 mm",
        framedPanel: "Panou cu ramă"
      },

      denumireModulGard(modul) {
        const key = moduleNameKey(modul.tip, modul.tip_poarta_mare, modul.tip_stalp)
        // a type with no key only ever comes from an offer saved with something since retired
        let name = (key && this.moduleNames[key]) || String(modul.tip || "").replace(/_/g, " ")
        if (modul.pp_included) name += " cu poarta pietonala integrata"
        return name
      },

      formatParagraph(textContent, alignment, textType) {
        return {
          type: "paragraph",
          attrs: { textAlign: alignment },
          content: [{
            type: "text",
            text: textContent,
            ...(textType && { marks: [{ type: textType }] })
          }]
        }
      },

      // Main document structure builder
      buildDocumentStructure() {
        return {
          type: "doc",
          content: [
            // Header
            {
              type: "heading",
              attrs: {
                textAlign: "center",
                level: 1,
              },
              content: [
                {
                  type: "text",
                  text: `ACT ADITIONAL NR. ${this.aditionalExtra.id_aditional
                    } / ${DateTime.fromISO(String(this.aditionalExtra.data)).toFormat("dd.MM.yyyy")}`,
                },
              ],
            },
            {
              type: "heading",
              attrs: {
                textAlign: "center",
                level: 2,
              },
              content: [
                {
                  type: "text",
                  text: "la CONTRACTUL DE EXECUTIE LUCRARI",
                },
              ],
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "center",
              },
              content: [
                {
                  type: "text",
                  text: `nr: ${this.contract.id_contract} / ${DateTime.fromISO(String(this.contract.data)).toFormat("dd.MM.yyyy")}`,
                },
              ],
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
            },
            {
              type: "heading",
              attrs: {
                textAlign: "left",
                level: 2,
              },
              content: [
                {
                  type: "text",
                  text: "In baza acordului de vointa al partilor",
                },
              ],
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text: "INTRE:",
                },
              ],
            },
            this.formatParagraph(this.executantFirmaText, "left"),
            this.isMontajSeparat ? this.formatParagraph("SI", "left") : { type: "blockquote" },
            this.isMontajSeparat ? this.formatParagraph(this.montajFirma1Text, "left") : { type: "blockquote" },
            this.isMontajSeparat ? this.formatParagraph("SAU", "left") : { type: "blockquote" },
            this.isMontajSeparat ? this.formatParagraph(this.montajFirma2Text, "left") : { type: "blockquote" },
            this.isMontajSeparat ? this.formatParagraph("SAU", "left") : { type: "blockquote" },
            this.isMontajSeparat ? this.formatParagraph(this.montajFirma3Text, "left") : { type: "blockquote" },
            this.formatParagraph("SI", "left"),
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `${this.isMontajSeparat ? "3" : "2"}. ${this.contract.nume.toUpperCase()}, ${this.adresaClient} telefon: ${this.contract.tel}, email: ${this.contract.email} in calitate de BENEFICIAR`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text: `Prin prezentul act aditional la contractul de executie lucrari nr: ${this.contract.id_contract
                    } / ${DateTime.fromISO(String(this.contract.data)).toFormat("dd.MM.yyyy")} se aduc urmatoarele modificari:`,
                },
              ],
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text:
                    "Art.1. Beneficiarul are finalizata partea de lucrare care cade in sarcina sa si executantul poate lua masuratorile finale.",
                },
              ],
            },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text: `Art.2. Oferta nr. ${this.previousOfferId} din data de ${this.previousOfferDate} si pretul de ${(fenceValueWithVat(this.previousOffer) + otherValueWithVat(this.previousOffer)).toFixed(2)} euro, ${this.previousOfferOrigin}, va fi inlocuita cu oferta nr. ${this.aditionalExtra.new_offer_id} din data ${DateTime.fromISO(String(this.aditionalExtra.date_new_offer)).toFormat("dd.MM.yyyy")} si pretul de ${(fenceValueWithVat(this.aditionalExtra) + otherValueWithVat(this.aditionalExtra)).toFixed(2)} euro, care va fi parte integranta din contract si care va fi calculata conform elementelor cu dimensiunile de mai jos, dimensiuni ce sunt calculate in urma masuratorilor finale din data de ....................... .`,
                },
              ],
            },
            {
              type: "bulletList",
              content: this.moduleGard,
            },
            {
              type: "bulletList",
              content: this.moduleCustom,
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "paragraph",
              attrs: {
                textAlign: "left",
              },
              content: [
                {
                  type: "text",
                  text: `Prezentul act adiţional a fost încheiat astazi ${DateTime.fromISO(String(this.aditionalExtra.data)).toFormat("dd.MM.yyyy")} in ${this.exemplareContract} exemplare, cate unul pentru fiecare parte.`,
                },
              ],
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            // Signatures
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "EXECUTANT"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: this.executantDetails.name }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: this.executantDetails.legal_representative }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "BENEFICIAR"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.nume.toUpperCase() }]
            },
            this.contract.persoana ? {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.persoana }]
            } : { type: "blockquote" },
            { type: "paragraph", attrs: { textAlign: "left" } },
            this.isMontajSeparat ? this.formatParagraph("MONTATOR", "left", "bold") : { type: "blockquote" }
          ]
        }
      },

      // PDF Generation
      async generatePdf(documentText) {
        const doc = await pdf_contract.createContractPDF(documentText.content, contract.country, CONTRACT_FORM_CODE)
        return doc
      }
    }
  }
}